import type { Customer, CustomerDevice } from './entity';

/**
 * Entity-level customer data derivations — pure functions over already-resolved
 * wire data (mirrors the predicate pattern used by other `services/db` entities,
 * e.g. `role`/`delivery`).
 *
 * Presentational concerns (device count/limit buckets, status badges, channel
 * and related-object display labels) intentionally do NOT live here — per review,
 * those belong in the feature/page that renders them, not the data layer.
 */

// Attribute keys the backend sets to mark a test recipient. Attribute values
// arrive as strings on the wire, so compare against the string form.
const TEST_USER_ATTR = 'cio_test_user';
const TEST_USER_DOUBLE_OPT_IN_ATTR = 'cio_double_opt_in';

/** True when the profile is flagged as a test recipient. */
export function isTestUser(attributes: Record<string, string> | undefined): boolean {
  return attributes?.[TEST_USER_ATTR] === 'true';
}

/** True once a test recipient has confirmed the double-opt-in email. */
export function hasConfirmedDoubleOptIn(attributes: Record<string, string> | undefined): boolean {
  return attributes?.[TEST_USER_DOUBLE_OPT_IN_ATTR] === 'true';
}

/**
 * The profile's internal `cio_id` identifier, if present. Bulk mutations key
 * off it; a profile can lack one (callers count those as failures up front).
 */
export function cioId(customer: Pick<Customer, 'identifiers'>): string | undefined {
  const id = customer.identifiers.cio_id;
  return id == null || id === '' ? undefined : id;
}

/** Subscription state — a profile is subscribed unless it has been unsubscribed. */
export function isSubscribed(customer: Pick<Customer, 'unsubscribed'>): boolean {
  return customer.unsubscribed !== true;
}

/** The signup timestamp attribute (`created_at`), if present. */
export function signedUp(attributes: Record<string, string> | undefined): string | undefined {
  return attributes?.created_at;
}

/**
 * A profile's devices as a typed list. Devices arrive embedded in the customer
 * GET (there is no separate devices endpoint); `Customer.devices` is now typed
 * (customerio/services#23464), so this just normalizes the absent case to an
 * empty list — a single accessor call sites use instead of reaching into
 * `customer.devices` themselves.
 */
export function customerDevices(customer: Pick<Customer, 'devices'>): CustomerDevice[] {
  return customer.devices ?? [];
}

/** Devices most-recently-used first (Ember sorted `lastUsed:desc`). */
export function sortDevices(devices: readonly CustomerDevice[] | undefined): CustomerDevice[] {
  return [...(devices ?? [])].sort((a, b) => (b.last_used ?? 0) - (a.last_used ?? 0));
}
